import { StyleSheet, css } from "aphrodite/no-important";
import {
  getData,
  processData,
  getCategoryColor,
  generateCategoryStatus,
} from "./utils";
import { useEffect, useState, useRef } from "react";

import Category from "./Category";
import Sidebar from "./Sidebar";
import Years from "./Years";

const DEFAULT_SCALE_FACTOR = 2;

function App() {
  const [eventsData, setEventsData] = useState({});
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryStatus, setCategoryStatus] = useState({});
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mousePositionX, setMousePositionX] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scaleFactor, setScaleFactor] = useState(DEFAULT_SCALE_FACTOR);
  const [firstYear, setFirstYear] = useState();
  const [lastYear, setLastYear] = useState();

  const timelineRef = useRef();

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getData();
        const { eventsData, firstYear, lastYear } = processData(data);
        setEventsData(eventsData);
        setFirstYear(firstYear);
        setLastYear(lastYear);

        const categoriesData = Object.keys(eventsData);
        setCategories(categoriesData);

        setCategoryStatus(generateCategoryStatus(categoriesData));

        setIsLoading(false);
      } catch (err) {
        console.log(err);
        setHasError(true);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const timelineRefCopy = timelineRef.current;
    if (!isLoading) {
      setScrollPosition(3300);
      timelineRefCopy.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (timelineRefCopy) {
        timelineRefCopy.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      timelineRef.current.scrollLeft = scrollPosition;
    }
  }, [isLoading, scaleFactor, scrollPosition]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const handleSideBarClick = () => {
    setIsSideBarOpen(!isSideBarOpen);
  };

  const handleMouseMove = (event) => {
    const offsetToAvoidScrollBlock = 5;
    setMousePositionX(event.clientX - offsetToAvoidScrollBlock);
  };

  const handleScroll = (event) => {
    setScrollPosition(timelineRef.current.scrollLeft);
  };

  const handleZoom = (change) => {
    if (scaleFactor === 1 && change < 0) return;
    const windowWidth = window.innerWidth;
    const windowCenter = windowWidth / 2;

    const preZoomScrollYear =
      (timelineRef.current.scrollLeft - 25 + windowCenter) / scaleFactor +
      firstCentury;
    const postZoomScrollPos =
      (preZoomScrollYear - firstCentury) * (scaleFactor + change) +
      25 -
      windowCenter;
    setScrollPosition(postZoomScrollPos);
    setScaleFactor(scaleFactor + change);
  };

  if (hasError) {
    return (
      <div className={css([styles.app, styles.loading])}>
        Oops, something went wrong. try reloading the page.
      </div>
    );
  }

  if (isLoading) {
    return <div className={css([styles.app, styles.loading])}>Loading…</div>;
  }

  const leftMargin = 22;
  const showRuler = mousePositionX ? true : false;
  const firstCentury = Math.floor(firstYear / 100) * -100;
  const year = !timelineRef.current
    ? undefined
    : Math.ceil(
        (mousePositionX - leftMargin) / scaleFactor -
          firstCentury +
          scrollPosition / scaleFactor
      );

  return (
    <div className={css(styles.app)}>
      <div className={css(styles.topMenu)}>
        <div className={css(styles.brand)} onClick={handleSideBarClick}>
          <svg
            className={css(styles.sliderLink)}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
          >
            <path d="M0 96C0 78.3 14.3 64 32 64H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32H416c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H416c17.7 0 32 14.3 32 32z" />
          </svg>
          <div className={css(styles.title)}>World History Timeline</div>
        </div>
        <div className={css(styles.zoomControls)}>
          <div
            className={css(styles.zoomButton)}
            onClick={() => {
              handleZoom(1);
            }}
          >
            ＋
          </div>
          <div
            className={css(styles.zoomButton)}
            onClick={() => {
              handleZoom(-1);
            }}
            style={{ paddingBottom: "2px" }}
          >
            —
          </div>
        </div>
      </div>
      <Sidebar
        categories={categories}
        categoryStatus={categoryStatus}
        setCategoryStatus={setCategoryStatus}
        isOpen={isSideBarOpen}
      />
      <div ref={timelineRef} className={css(styles.timelineWrapper)}>
        <div
          className={css(showRuler ? styles.ruler : styles.hidden)}
          data-year={year}
          style={{ left: `${mousePositionX}px` }}
        ></div>
        <div className={css(styles.timeline)}>
          <Years
            eventsData={eventsData}
            scaleFactor={scaleFactor}
            firstYear={firstYear}
            lastYear={lastYear}
          />
          <div className={css(styles.eventGroups)}>
            {categories.map((category, i) => (
              <Category
                key={category}
                cat={category}
                categoryColor={getCategoryColor(i)}
                eventsData={eventsData}
                isVisible={categoryStatus[category]}
                scaleFactor={scaleFactor}
                firstYear={firstYear}
                lastYear={lastYear}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const topMenuHeight = "47px";

const styles = StyleSheet.create({
  loading: {
    display: "flex",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "space-around",
  },
  app: {
    fontFamily: "helvetica",
    color: "rgba(0,0,0,0.9)",
  },
  topMenu: {
    borderBottom: "1px solid #EEE",
    padding: "9px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    userSelect: "none",
    position: "fixed",
    width: "100%",
    background: "#FFF",
    zIndex: "2",
    boxSizing: "border-box",
  },
  sliderLink: {
    height: "15px",
    marginRight: "10px",
    fill: "rgb(143 161 186)",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  title: {
    fontWeight: "bold",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    minWidth: "250px",
  },
  zoomControls: {
    display: "flex",
  },
  zoomButton: {
    minWidth: "25px",
    minHeight: "25px",
    marginLeft: "5px",
    border: "1px solid #eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "100%",
    ":hover": {
      background: "#eee",
    },
  },
  timelineWrapper: {
    maxWidth: "100%",
    overflowX: "scroll",
    paddingTop: topMenuHeight,
  },
  ruler: {
    minHeight: `calc(100vh - ${topMenuHeight})`,
    minWidth: "3px",
    background: "black",
    position: "absolute",
    top: topMenuHeight,
    left: "100px",
    zIndex: "2",
    ":before": {
      fontSize: "11px",
      position: "absolute",
      width: "max-content",
      color: "#FFF",
      backgroundColor: "#000",
      borderRadius: "0px 0px 6px 6px",
      padding: "3px 6px",
      top: "0px",
      content: "attr(data-year)",
      maxWidth: "100px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 5,
    },
  },
  hidden: {
    display: "none",
  },
  timeline: {
    paddingLeft: "25px",
    position: "relative",
  },
  eventGroups: {
    width: "100%",
    paddingTop: "5px",
    position: "absolute",
    top: 0,
  },
});

export default App;
